import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft, Wallet, IndianRupee, TrendingUp, Users, Percent,
  CheckCircle, Clock, Loader2, ArrowUpDown,
} from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import { cn } from '../../../utils/tailwindUtils';
import {
  getEngageStatusBadge, getEngageSourceBadge,
  ENGAGE_STATUS_LABEL, ENGAGE_SOURCE_LABEL,
} from '../../../utils/engageStageBadge';


const formatINR = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtDate   = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';


const TIER_META = {
  quoted:   { label: 'Quoted',   tone: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  catalog:  { label: 'Catalog',  tone: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300' },
  fallback: { label: 'Fallback', tone: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300' },
};


export default function RevenueSourceReport() {
  const { source } = useParams();
  const navigate = useNavigate();
  const [urlParams] = useSearchParams();
  const { fetch: apiFetch } = useApiMethods();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('pipeline');    // 'pipeline' | 'realised'

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const q = new URLSearchParams();
        ['from', 'to', 'arpp'].forEach(k => { if (urlParams.get(k)) q.set(k, urlParams.get(k)); });
        const resp = await apiFetch(`engage/analytics/revenue-source/${source}/?${q.toString()}`);
        setData(resp?.data || resp?.data?.data || null);
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load report');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, urlParams]);

  if (loading) return (
    <div className="p-8 text-center text-gray-500 flex items-center justify-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>
  );
  if (error)   return <div className="p-4 text-red-600">{error}</div>;
  if (!data)   return <div className="p-4">Nothing to show.</div>;

  const s = data.summary;
  const tc = data.tier_counts;
  const realised = data.realised || [];
  const pipeline = data.pipeline || [];
  const rows = tab === 'realised' ? realised : pipeline;

  return (
    <div className="p-4 max-w-[1400px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/dashboard')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Dashboard
      </button>

      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-sm">
            <Wallet className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white inline-flex items-center gap-2">
              Revenue deep dive
              <span className={cn('text-[10px] font-medium px-2 py-0.5 rounded-full', getEngageSourceBadge(source))}>
                {ENGAGE_SOURCE_LABEL[source] || source}
              </span>
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {data.range.from} → {data.range.to} · ARPP fallback ₹{formatINR(data.assumptions.avg_arpp)}
            </p>
          </div>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <SummaryCard label="Historical enquiries"   value={s.total_all}                                      icon={Users}      tone="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400" />
        <SummaryCard label="Ever converted"         value={s.converted_all}                                  icon={CheckCircle} tone="bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400" />
        <SummaryCard label="Historical conv-rate"   value={`${s.conversion_rate_pct}%`}                       icon={Percent}    tone="bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400" />
        <SummaryCard label="Realised (period)"      value={`₹ ${formatINR(s.realised_revenue)}`}              icon={IndianRupee} tone="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400" valueTone="text-emerald-600 dark:text-emerald-400" />
        <SummaryCard label="Pipeline (expected)"    value={`₹ ${formatINR(s.pipeline_revenue)}`}              icon={Clock}       tone="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400" valueTone="text-blue-600 dark:text-blue-400" sub={`Full value ₹${formatINR(s.pipeline_full_value)} × ${s.conversion_rate_pct}%`} />
        <SummaryCard label="Projected total"        value={`₹ ${formatINR(s.projected_total)}`}               icon={TrendingUp} tone="bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400" sub={s.spend > 0 ? `Spend ₹${formatINR(s.spend)} · ROI ${s.roi_multiple ?? '—'}×` : ''} />
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden shadow-sm">
        <div className="flex items-center border-b border-gray-100 dark:border-gray-700">
          <TabButton active={tab === 'pipeline'} onClick={() => setTab('pipeline')}
            counts={tc.pipeline} label="Pipeline" tone="bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300" />
          <TabButton active={tab === 'realised'} onClick={() => setTab('realised')}
            counts={tc.realised} label="Realised (period)" tone="bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300" />
        </div>

        {/* Tier legend */}
        <div className="px-4 py-1.5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3 text-[10px] text-gray-500 dark:text-gray-400">
          <span className="uppercase tracking-wide font-medium">Value-tier legend:</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-green-500 rounded-sm" /> Quoted (per-enquiry value)</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-teal-400 rounded-sm" /> Catalog match on procedure</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-gray-300 dark:bg-gray-600 rounded-sm" /> Fallback (dept/global ARPP)</span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Enquiry</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-left font-medium">Department</th>
                <th className="px-4 py-2 text-left font-medium">Procedure</th>
                <th className="px-4 py-2 text-right font-medium">Value used ₹</th>
                <th className="px-4 py-2 text-center font-medium">Tier</th>
                {tab === 'pipeline' && <th className="px-4 py-2 text-right font-medium">Expected ₹<br/><span className="normal-case font-normal text-[9px]">× {s.conversion_rate_pct}%</span></th>}
                <th className="px-4 py-2 text-left font-medium">Assigned to</th>
                <th className="px-4 py-2 text-right font-medium">
                  {tab === 'realised' ? 'Converted on' : 'Follow-up'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.length === 0 ? (
                <tr><td colSpan={tab === 'pipeline' ? 9 : 8} className="px-4 py-12 text-center text-gray-500">
                  {tab === 'realised' ? 'No converted enquiries in this period.' : 'No active enquiries.'}
                </td></tr>
              ) : rows.map(r => {
                const M = TIER_META[r.tier] || TIER_META.fallback;
                return (
                  <tr key={r.id} onClick={() => navigate(`/engage/enquiries/${r.id}`)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer">
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-blue-600 dark:text-blue-400">{r.name}</div>
                      <div className="text-[10px] text-gray-500">{r.mobile}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', getEngageStatusBadge(r.status))}>
                        {ENGAGE_STATUS_LABEL[r.status] || r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">{r.department || '—'}</td>
                    <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">{r.procedure || <span className="text-gray-400 italic">not specified</span>}</td>
                    <td className="px-4 py-2.5 text-right font-medium tabular-nums text-gray-900 dark:text-white">
                      ₹ {formatINR(r.value_used)}
                      {r.expected_value != null && r.expected_value !== r.value_used && (
                        <div className="text-[10px] text-gray-400">quote ₹{formatINR(r.expected_value)}</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', M.tone)}>
                        {M.label}
                      </span>
                    </td>
                    {tab === 'pipeline' && (
                      <td className="px-4 py-2.5 text-right tabular-nums text-blue-600 dark:text-blue-400 font-medium">
                        ₹ {formatINR(r.expected_contribution)}
                      </td>
                    )}
                    <td className="px-4 py-2.5 text-[12px] text-gray-600 dark:text-gray-400">{r.assigned_to_name || '—'}</td>
                    <td className="px-4 py-2.5 text-right text-[11px] text-gray-500">
                      {tab === 'realised' ? fmtDate(r.modified_date) : fmtDate(r.followup_datetime)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-gray-50 dark:bg-gray-900/40 text-[11px] font-medium">
                <tr>
                  <td className="px-4 py-2 text-gray-500" colSpan={4}>
                    <span className="text-green-600 dark:text-green-400">{tc[tab].quoted}</span> quoted
                    {' + '}
                    <span className="text-teal-600 dark:text-teal-400">{tc[tab].catalog}</span> catalog
                    {' + '}
                    <span className="text-gray-500">{tc[tab].fallback}</span> fallback
                    {' = '}<b>{tc[tab].total}</b> enquiries
                  </td>
                  <td className="px-4 py-2 text-right text-gray-900 dark:text-white tabular-nums font-bold">
                    ₹ {formatINR(rows.reduce((s, r) => s + (r.value_used || 0), 0))}
                  </td>
                  <td />
                  {tab === 'pipeline' && (
                    <td className="px-4 py-2 text-right text-blue-600 dark:text-blue-400 tabular-nums font-bold">
                      ₹ {formatINR(s.pipeline_revenue)}
                    </td>
                  )}
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Explanation card */}
      <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-800/40 text-[11px] text-emerald-700 dark:text-emerald-300">
        <p className="font-semibold mb-1">How this is calculated</p>
        <ul className="ml-4 list-disc space-y-0.5">
          <li><b>Realised</b> ₹{formatINR(s.realised_revenue)} = sum of value used for enquiries converted between {data.range.from} and {data.range.to}.</li>
          <li><b>Pipeline</b> ₹{formatINR(s.pipeline_revenue)} = (sum of value used for active enquiries) × <b>{s.conversion_rate_pct}%</b> historical conversion rate.</li>
          <li><b>Projected total</b> ₹{formatINR(s.projected_total)} = Realised + Pipeline.</li>
          {s.spend > 0 && <li><b>ROI</b> {s.roi_multiple ?? '—'}× = Projected / spend (₹{formatINR(s.spend)}).</li>}
        </ul>
      </div>
    </div>
  );
}


function SummaryCard({ label, value, icon: Icon, tone, sub, valueTone }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">{label}</p>
          <p className={cn('text-xl font-bold mt-1', valueTone || 'text-gray-900 dark:text-white')}>{value}</p>
          {sub && <p className="text-[10px] text-gray-500 mt-0.5 truncate">{sub}</p>}
        </div>
        <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0', tone)}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}


function TabButton({ active, onClick, counts, label, tone }) {
  return (
    <button onClick={onClick} className={cn(
      'flex-1 sm:flex-none px-4 py-3 text-sm font-medium transition-colors relative',
      active
        ? 'text-gray-900 dark:text-white border-b-2 border-emerald-500'
        : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300',
    )}>
      <span className="inline-flex items-center gap-1.5">
        {label}
        <span className={cn('inline-block px-1.5 py-0.5 rounded-full text-[10px] font-medium', tone)}>
          {counts.total}
        </span>
      </span>
    </button>
  );
}
